import React, { useEffect, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

export default function VipFeedback() {
  const [guards, setGuards] = useState([]);
  const [remark, setRemark] = useState("");
  const [vipId, setVipId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingFeedback, setFetchingFeedback] = useState(false);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);

  const [feedbackList, setFeedbackList] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [submitting, setSubmitting] = useState(false);

  const isMobile = windowWidth <= 991;
  const vipToken = localStorage.getItem("vipToken");
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [assignmentId, setAssignmentId] = useState(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch assigned guards
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        if (!vipToken) {
          setError("VIP token missing");
          return;
        }

        const decoded = jwtDecode(vipToken);
        const username = decoded.username || decoded.sub || decoded.email;

        const profileRes = await axios.get(
          `${BASE_URL}/api/categories/profile`,
          {
            params: { username },
            headers: { Authorization: `Bearer ${vipToken}` },
          }
        );

        const id = profileRes.data?.id;
        if (!id) {
          setError("VIP ID not found");
          return;
        }

        setVipId(id);

        const assignmentRes = await axios.get(
          `${BASE_URL}/api/assignments/${id}`,
          {
            headers: { Authorization: `Bearer ${vipToken}` },
          }
        );

        const assignmentData = assignmentRes.data;
        setAssignmentId(assignmentData.id);

        const officers = Array.isArray(assignmentData?.officer)
          ? assignmentData.officer
          : [];

        const officersWithSelection = officers.map((officer) => ({
          ...officer,
          selected: false,
          assignmentId: assignmentData.id,
        }));

        setGuards(officersWithSelection);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch guards");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [vipToken, BASE_URL]);

  // Fetch submitted feedback from API
 // Fetch submitted feedback from API
const fetchFeedbackList = async () => {
  if (!vipToken || !vipId) return;
  
  setFetchingFeedback(true);
  try {
    const response = await axios.get(
      `${BASE_URL}/api/vipremarks/${vipId}`,
      {
        params: { vipId },
        headers: { Authorization: `Bearer ${vipToken}` },
      }
    );
    
    // Ensure feedbackList is always an array
    const responseData = response.data || {};
    
    // Handle different response structures
    let feedbackArray = [];
    
    if (Array.isArray(responseData)) {
      // If response.data is already an array
      feedbackArray = responseData;
    } else if (responseData.data && Array.isArray(responseData.data)) {
      // If response.data has a data property that's an array
      feedbackArray = responseData.data;
    } else if (responseData.feedbacks && Array.isArray(responseData.feedbacks)) {
      // If response.data has a feedbacks property that's an array
      feedbackArray = responseData.feedbacks;
    } else if (responseData.results && Array.isArray(responseData.results)) {
      // If response.data has a results property that's an array
      feedbackArray = responseData.results;
    } else if (typeof responseData === 'object') {
      // If it's a single object, wrap it in an array
      feedbackArray = [responseData];
    }
    
    console.log('Feedback data:', responseData); // For debugging
    console.log('Processed array:', feedbackArray); // For debugging
    
    setFeedbackList(feedbackArray);
  } catch (err) {
    console.error("Failed to fetch feedback:", err);
    toast.error("Failed to load feedback history");
  } finally {
    setFetchingFeedback(false);
  }
};
  // Fetch feedback when component mounts and after submissions
  useEffect(() => {
    if (vipId && vipToken) {
      fetchFeedbackList();
    }
  }, [vipId, vipToken]);

  // Handle checkbox selection
  const handleGuardSelect = (guardId) => {
    setGuards((prevGuards) =>
      prevGuards.map((guard) =>
        guard.id === guardId ? { ...guard, selected: !guard.selected } : guard
      )
    );
  };

  // Select all guards
  const handleSelectAll = () => {
    const allSelected = guards.every((guard) => guard.selected);
    setGuards((prevGuards) =>
      prevGuards.map((guard) => ({
        ...guard,
        selected: !allSelected,
      }))
    );
  };

  // Clear all selections
  const handleClearAll = () => {
    setGuards((prevGuards) =>
      prevGuards.map((guard) => ({
        ...guard,
        selected: false,
      }))
    );
  };

  // Get selected guard IDs
  const getSelectedGuardIds = () => {
    return guards.filter((guard) => guard.selected).map((guard) => guard.id);
  };

  const handleSubmit = async () => {
    const selectedIds = getSelectedGuardIds();

    if (selectedIds.length === 0) {
      toast.error("Please select at least one officer");
      return;
    }

    if (!remark.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/vipremarks`,
        {
          currentAssignmentId: assignmentId,
          officerId: selectedIds,
          remarks: remark,
          subject: "Feedback",
          vipId: vipId
        },
        {
          headers: { Authorization: `Bearer ${vipToken}` },
        }
      );

      handleClearAll();
      setRemark("");
      await fetchFeedbackList();

      toast.success(
        `Feedback submitted successfully to ${selectedIds.length} officer(s)`
      );
    } catch (err) {
      console.error(err);
      if (err.response?.status === 400) {
        toast.error("Invalid request. Please check your input.");
      } else if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
      } else if (err.response?.status === 500) {
        toast.error("Your Feedback already exits.");
      } else {
        toast.error("Failed to submit feedback");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (feedbackId) => {
    setExpandedRows((prev) =>
      prev.includes(feedbackId)
        ? prev.filter((id) => id !== feedbackId)
        : [...prev, feedbackId]
    );
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading assigned officers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-card">
        <i className="fas fa-exclamation-triangle fa-3x"></i>
        <h3>{error}</h3>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#a855f7',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="feedback-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Provide Feedback</h1>
              <p>
                Share your valuable experience with assigned security officers
              </p>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="page-main">
          {/* Feedback Form Card - KEEPING EXACTLY THE SAME */}
          <section className="form-card">
            <h2>Select Officer(s) & Share Your Feedback</h2>

            <div className="form-group">
              <div className="selection-header">
                <label className="form-label">
                  <i className="fas fa-user-shield"></i> Assigned Officer(s)
                </label>
                {guards.length > 0 && (
                  <div className="selection-controls">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="selection-btn"
                    >
                      <i className="fas fa-check-double"></i> Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="selection-btn clear"
                    >
                      <i className="fas fa-times"></i> Clear All
                    </button>
                  </div>
                )}
              </div>

              {guards.length === 0 ? (
                <div className="info-note">
                  <i className="fas fa-info-circle"></i>
                  <p>No officers currently assigned to you.</p>
                </div>
              ) : (
                <div className="officers-grid">
                  {guards.map((officer) => (
                    <div
                      key={officer.id}
                      className={`officer-card ${
                        officer.selected ? "selected" : ""
                      }`}
                      onClick={() => handleGuardSelect(officer.id)}
                    >
                      <div className="officer-checkbox">
                        <input
                          type="checkbox"
                          checked={officer.selected}
                          onChange={() => handleGuardSelect(officer.id)}
                          id={`officer-${officer.id}`}
                        />
                        <label htmlFor={`officer-${officer.id}`}></label>
                      </div>
                      <div className="officer-info">
                        <div className="officer-name">{officer.name}</div>
                        <div className="officer-details">
                          <span className="officer-id">
                            Assignment ID: {officer.assignmentId}
                          </span>
                          <span className="officer-id">ID: {officer.id}</span>
                          <span className="officer-rank">{officer.rank}</span>
                        </div>
                      </div>
                      {officer.selected && (
                        <div className="selected-indicator">
                          <i className="fas fa-check-circle"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {getSelectedGuardIds().length > 0 && (
                <div className="selection-summary">
                  <i className="fas fa-users"></i>
                  <span>
                    {getSelectedGuardIds().length} officer(s) selected
                  </span>
                </div>
              )}
            </div>

            {getSelectedGuardIds().length > 0 && (
              <>
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-star"></i> Your Feedback
                  </label>
                  <textarea
                    rows="5"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder={`We appreciate your honest feedback about the ${
                      getSelectedGuardIds().length
                    } selected officer(s)...`}
                    className="form-textarea"
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!remark.trim() || submitting}
                  className="submit-btn"
                >
                  {submitting ? (
                    <>
                      <div className="submit-spinner"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      {remark.trim()
                        ? `Submit to ${getSelectedGuardIds().length} Officer(s)`
                        : "Enter feedback to submit"}
                    </>
                  )}
                </button>
              </>
            )}
          </section>

          {/* Submitted Feedbacks Section - MODIFIED TABLE STRUCTURE ONLY */}
          <section className="feedback-list-card">
            <div className="feedback-list-header">
              <h2>
                <i className="fas fa-comments"></i> Your Submitted Feedback
              </h2>
              <button 
                onClick={fetchFeedbackList} 
                className="refresh-btn"
                disabled={fetchingFeedback}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#a855f7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {fetchingFeedback ? (
                  <>
                    <div className="mini-spinner" style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt"></i>
                    Refresh
                  </>
                )}
              </button>
            </div>

            {feedbackList.length === 0 ? (
              <div className="info-note">
                <i className="fas fa-comment-slash"></i>
                <p>No feedback submitted yet. Your feedback will appear here.</p>
              </div>
            ) : !isMobile ? (
              /* Desktop Table - MODIFIED STRUCTURE */
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Feedback ID</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackList
                      .sort((a, b) => new Date(b.created) - new Date(a.created))
                      .map((feedback) => (
                        <React.Fragment key={feedback.id}>
                          {/* Main Row with Assignment ID, Message, Date */}
                          <tr className="main-row">
                            <td>
                              <strong>{feedback.id}</strong>
                            </td>
                            <td className="message-cell">
                              <div className="message-preview">
                                {feedback.remarks.length > 100
                                  ? `${feedback.remarks.substring(0, 100)}...`
                                  : feedback.remarks}
                              </div>
                            </td>
                            <td>
                              {formatDate(feedback.created)}
                            </td>
                            <td style={{ textAlign: 'center', width: '50px' }}>
                              <button
                                onClick={() => toggleRowExpansion(feedback.id)}
                                className="expand-btn"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#a855f7',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem'
                                }}
                              >
                                <i 
                                  className={`fas fa-chevron-${
                                    expandedRows.includes(feedback.id) ? 'up' : 'down'
                                  }`}
                                ></i>
                              </button>
                            </td>
                          </tr>
                          
                          {/* Expanded Row with Officer Details */}
                          {expandedRows.includes(feedback.id) && (
                            <tr className="expanded-row">
                              <td colSpan="4" style={{ backgroundColor: '#f8f7ff', padding: '0' }}>
                                <div style={{ padding: '1.5rem' }}>
                                  <h4 style={{ 
                                    color: '#6b21a8', 
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                  }}>
                                    <i className="fas fa-user-shield"></i>
                                    Officers ({feedback.officersRemarks?.length || 0})
                                  </h4>
                                  
                                  {feedback.officersRemarks && feedback.officersRemarks.length > 0 ? (
                                    <div style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                      gap: '1rem' 
                                    }}>
                                      {feedback.officersRemarks.map((officer) => (
                                        <div key={officer.id} style={{ 
                                          background: 'white',
                                          borderRadius: '10px',
                                          padding: '1.5rem',
                                          border: '1px solid #e9d5ff',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                        }}>
                                          <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '1rem',
                                            paddingBottom: '0.5rem',
                                            borderBottom: '1px dashed #e9d5ff'
                                          }}>
                                            <div style={{ fontWeight: '600', color: '#4c1d95' }}>
                                              {officer.name}
                                            </div>
                                            <span style={{ 
                                              background: '#f3e8ff',
                                              color: '#6b21a8',
                                              padding: '0.3rem 0.8rem',
                                              borderRadius: '4px',
                                              fontSize: '0.85rem',
                                              fontWeight: '600'
                                            }}>
                                              {officer.rank}
                                            </span>
                                          </div>
                                          
                                          <div style={{ 
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '0.8rem'
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <i className="fas fa-id-card" style={{ color: '#a855f7', width: '16px' }}></i>
                                              <span style={{ fontSize: '0.9rem', color: '#6b46c1' }}>ID: {officer.id}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <i className="fas fa-envelope" style={{ color: '#a855f7', width: '16px' }}></i>
                                              <span style={{ fontSize: '0.9rem', color: '#6b46c1' }}>{officer.email}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <i className="fas fa-phone" style={{ color: '#a855f7', width: '16px' }}></i>
                                              <span style={{ fontSize: '0.9rem', color: '#6b46c1' }}>+91 {officer.contactno}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <i className="fas fa-venus-mars" style={{ color: '#a855f7', width: '16px' }}></i>
                                              <span style={{ fontSize: '0.9rem', color: '#6b46c1' }}>{officer.gender}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <i className="fas fa-briefcase" style={{ color: '#a855f7', width: '16px' }}></i>
                                              <span style={{ fontSize: '0.9rem', color: '#6b46c1' }}>{officer.experience} years exp.</span>
                                            </div>
                                           
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ 
                                      textAlign: 'center', 
                                      padding: '2rem',
                                      color: '#7c3aed',
                                      background: 'rgba(168, 85, 247, 0.1)',
                                      borderRadius: '10px'
                                    }}>
                                      <i className="fas fa-user-slash" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
                                      No officers information available
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile Cards - KEEPING EXACTLY THE SAME */
              <div className="mobile-feedback">
                {feedbackList
                  .sort((a, b) => new Date(b.created) - new Date(a.created))
                  .map((feedback) => (
                    <div key={feedback.id} className="mobile-feedback-card">
                      <div className="feedback-header">
                        <div>
                          <strong>Assignment #{feedback.id}</strong>
                          <div style={{ fontSize: '0.9rem', color: '#6b46c1' }}>
                            ID: {feedback.officerId}
                          </div>
                        </div>
                        <span className="subject-badge">{feedback.subject || "Feedback"}</span>
                      </div>
                      <div className="feedback-body">
                        <p>{feedback.remarks}</p>
                        <div className="feedback-meta">
                          <div className="feedback-assignment">
                            <i className="fas fa-id-card"></i>
                            Assignment: {feedback.currentAssignmentId || feedback.assignmentId}
                          </div>
                          <div className="feedback-date">
                            <i className="far fa-calendar"></i>
                            {new Date(feedback.created).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </section>
        </main>
      </div>

      {/* CSS - KEEPING EXACTLY THE SAME */}
      <style jsx>{`
        .feedback-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0d4ff 0%, #f0e6ff 100%);
          color: #4c1d95;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }

        .page-header {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(168, 85, 247, 0.4);
          padding: 1.2rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #9333ea;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.95rem;
          color: #6b46c1;
        }

        .page-main {
          padding: 1.5rem 2rem;
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        @media (max-width: 768px) {
          .page-main {
            padding: 1rem;
          }
          .page-header {
            padding: 1rem 1.5rem;
          }
        }

        .form-card,
        .feedback-list-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 2rem;
          border: 1px solid rgba(168, 85, 247, 0.5);
          transition: all 0.3s ease;
        }

        .form-card:hover,
        .feedback-list-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 35px rgba(168, 85, 247, 0.25);
        }

        .feedback-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .form-card h2,
        .feedback-list-card h2 {
          font-size: 1.6rem;
          font-weight: 700;
          color: #6b21a8;
          margin-bottom: 0;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .selection-controls {
          display: flex;
          gap: 0.8rem;
        }

        .selection-btn {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          border: 2px solid #a855f7;
          background: white;
          color: #6b21a8;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s ease;
        }

        .selection-btn:hover {
          background: #a855f7;
          color: white;
        }

        .selection-btn.clear {
          border-color: #ef4444;
          color: #dc2626;
        }

        .selection-btn.clear:hover {
          background: #ef4444;
          color: white;
        }

        .officers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
          max-height: 300px;
          overflow-y: auto;
          padding: 0.5rem;
          border: 1px solid #e9d5ff;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
        }

        @media (max-width: 768px) {
          .officers-grid {
            grid-template-columns: 1fr;
          }
        }

        .officer-card {
          display: flex;
          align-items: center;
          padding: 1rem;
          border-radius: 10px;
          border: 2px solid #e9d5ff;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .officer-card:hover {
          border-color: #a855f7;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(168, 85, 247, 0.2);
        }

        .officer-card.selected {
          border-color: #10b981;
          background: rgba(16, 185, 129, 0.05);
        }

        .officer-checkbox {
          margin-right: 1rem;
        }

        .officer-checkbox input[type="checkbox"] {
          display: none;
        }

        .officer-checkbox label {
          width: 20px;
          height: 20px;
          border: 2px solid #a855f7;
          border-radius: 4px;
          display: block;
          cursor: pointer;
          position: relative;
          transition: all 0.2s ease;
        }

        .officer-checkbox input[type="checkbox"]:checked + label {
          background: #10b981;
          border-color: #10b981;
        }

        .officer-checkbox input[type="checkbox"]:checked + label:after {
          content: "✓";
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 12px;
          font-weight: bold;
        }

        .officer-info {
          flex: 1;
        }

        .officer-name {
          font-weight: 600;
          color: #4c1d95;
          margin-bottom: 0.25rem;
        }

        .officer-details {
          display: flex;
          gap: 1rem;
          font-size: 0.9rem;
          color: #6b46c1;
          flex-wrap: wrap;
        }

        .officer-id {
          background: #f3e8ff;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
        }

        .officer-rank {
          font-style: italic;
        }

        .selected-indicator {
          margin-left: 0.5rem;
          color: #10b981;
        }

        .selection-summary {
          margin-top: 1rem;
          padding: 0.8rem;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.8rem;
          color: #059669;
          font-weight: 600;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-weight: 600;
          color: #581c87;
          margin-bottom: 0.6rem;
          font-size: 1.05rem;
        }

        .form-textarea {
          width: 100%;
          padding: 1rem;
          border-radius: 12px;
          border: 2px solid #d8b4fe;
          background: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #a855f7;
          box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.2);
        }

        .submit-btn {
          width: 100%;
          padding: 1.2rem;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
          transition: all 0.3s ease;
          margin-top: 1rem;
          position: relative;
        }

        .submit-btn:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .submit-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .info-note {
          text-align: center;
          padding: 2rem;
          color: #7c3aed;
          font-size: 1.1rem;
          background: rgba(168, 85, 247, 0.1);
          border-radius: 12px;
          margin-top: 1rem;
        }

        .info-note i {
          margin-bottom: 0.8rem;
          font-size: 2rem;
          opacity: 0.7;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .data-table {
          width: 100%;
          min-width: 800px;
          border-collapse: collapse;
          background: white;
        }

        .data-table thead {
          background: #a855f7;
          color: white;
        }

        .data-table th,
        .data-table td {
          padding: 1.2rem;
          text-align: left;
          border-bottom: 1px solid #e9d5ff;
        }

        .data-table th {
          font-weight: 600;
        }

        .data-table tbody tr:hover {
          background: rgba(168, 85, 247, 0.1);
        }

        .message-cell {
          max-width: 300px;
          word-wrap: break-word;
        }

        .subject-badge {
          background: #10b981;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
        }

        /* Mobile View */
        .mobile-feedback {
          display: flex;
          flex-direction: column;
          gap: 1.2rem;
        }

        .mobile-feedback-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 1.6rem;
          border-left: 5px solid #10b981;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
        }

        .feedback-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 0.8rem;
          border-bottom: 1px dashed rgba(16, 185, 129, 0.3);
        }

        .feedback-header strong {
          color: #581c87;
          font-size: 1.1rem;
        }

        .feedback-body p {
          color: #4c1d95;
          line-height: 1.6;
          margin-bottom: 1rem;
        }

        .feedback-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: #6b46c1;
        }

        .feedback-assignment,
        .feedback-date {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          opacity: 0.8;
        }

        .loading-container {
          text-align: center;
          padding: 5rem;
          color: #9333ea;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e9d5ff;
          border-top: 4px solid #a855f7;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1.5rem;
        }

        .error-card {
          text-align: center;
          padding: 5rem 2rem;
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          margin: 2rem;
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #dc2626;
        }

        .error-card i {
          margin-bottom: 1.5rem;
          opacity: 0.8;
        }

        .error-card h3 {
          font-size: 1.6rem;
          margin-bottom: 1rem;
        }

        /* Animation for expanded rows */
        @keyframes expand {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .expanded-row {
          animation: expand 0.3s ease-out;
        }
      `}</style>
    </>
  );
}